const BASE_URL = "https://api.growthstationco.com";

// ================= ERROR HELPER =================
class ApiError extends Error {
  constructor(response, data) {
    const msg =
      (typeof data === "string" && data.trim().slice(0, 200)) ||
      data?.message ||
      data?.error ||
      `Request failed (${response?.status})`;
    super(msg);
    this.response = response;
    this.status = response?.status;
    this.data = data;
  }
}

// ─── HEADERS ─────────────────────────────
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");

  return {
    "Content-Type": "application/json",
    Authorization: token || "",
  };
};

// ─── RESPONSE ────────────────────────────
async function parseResponseBody(response) {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

// ─── QUERY ───────────────────────────────
const buildQuery = (filters = {}) => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== "") {
      params.append(key, val);
    }
  });

  const str = params.toString();
  return str ? `?${str}` : "";
};

// ─────────────────────────────────────────
// TASKS
// ─────────────────────────────────────────

// GET ALL TASKS
export const getTasks = async (filters = {}) => {
  const res = await fetch(`${BASE_URL}/tasks${buildQuery(filters)}`, {
    headers: getAuthHeaders(),
  });

  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);

  return json;
};

// GET ONE TASK
export const getTaskById = async (id) => {
  const res = await fetch(`${BASE_URL}/tasks/${id}`, {
    headers: getAuthHeaders(),
  });

  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);

  return json;
};

// CREATE TASK
export const createTaskApi = async (data) => {
  const res = await fetch(`${BASE_URL}/tasks/add`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);

  return json;
};

// UPDATE TASK (ALL FIELDS INCLUDING STATUS)
export const updateTaskApi = async (id, data) => {
  const res = await fetch(`${BASE_URL}/tasks/update/${id}`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);

  return json;
};

// DELETE TASK
export const deleteTaskApi = async (id) => {
  const res = await fetch(`${BASE_URL}/tasks/${id}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });

  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);

  return json;
};

// GET DELETED TASKS
export const getDeletedTasks = async (page = 1, limit = 10) => {
  const res = await fetch(`${BASE_URL}/tasks/deleted?page=${page}&limit=${limit}`, {
    headers: getAuthHeaders(),
  });

  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);

  return json;
};

// RESTORE TASK
export const restoreTask = async (id) => {
  const res = await fetch(`${BASE_URL}/tasks/restore/${id}`, {
    method: "PATCH",
    headers: getAuthHeaders(),
  });

  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);

  return json;
};

// ─────────────────────────────────────────
// COMMENTS
// ─────────────────────────────────────────

// ADD COMMENT
export const addCommentApi = async (taskId, data) => {
  const res = await fetch(`${BASE_URL}/tasks/add-comment/${taskId}`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      text: data.comment,
    }),
  });

  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// EDIT COMMENT
export const editCommentApi = async (taskId, commentId, data) => {
  const res = await fetch(`${BASE_URL}/tasks/${taskId}/comment/${commentId}`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);

  return json;
};

// DELETE COMMENT
export const deleteCommentApi = async (taskId, commentId) => {
  const res = await fetch(`${BASE_URL}/tasks/${taskId}/comment/${commentId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });

  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);

  return json;
};

// ─────────────────────────────────────────
// FILE DELETE
// ─────────────────────────────────────────

export const deleteTaskFileApi = async (taskId, fileId, type = "attachment") => {
  console.log("DELETE FILE - taskId:", taskId, "fileId:", fileId, "type:", type);
  const res = await fetch(
    `${BASE_URL}/tasks/${taskId}/file?fileId=${fileId}&type=${type}`,
    {
      method: "DELETE",
      headers: getAuthHeaders(),
    }
  );

  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);

  return json;
};
// UPDATE TASK STATUS ONLY
export const updateTaskStatusApi = async (
  taskId,
  data
) => {
  const res = await fetch(
    `${BASE_URL}/tasks/status/${taskId}`,
    {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    }
  );

  const json = await parseResponseBody(res);

  if (!res.ok) throw new ApiError(res, json);

  return json;
};

export const uploadTaskFileApi = async (taskId, files) => {
  const token = localStorage.getItem("token");

  const formData = new FormData();

  Array.from(files).forEach((file) => {
    formData.append("attachments", file);
  });

  const res = await fetch(`${BASE_URL}/tasks/update/${taskId}`, {
    method: "PATCH",
    headers: {
      Authorization: token || "",
    },
    body: formData,
  });

  const json = await parseResponseBody(res);

  if (!res.ok) throw new ApiError(res, json);

  return json;
};

